import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, SpeakerHigh, CreditCard, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PRESETS = [500, 1000, 2500, 5000];

// =====================================================================
// FundVoiceModal — "fan-funded voice". Shown when a silo's owner hasn't funded
// premium voice. The visitor chips in; 100% tops up the creator's OpenAI voice
// credit (NOT a cash tip), which switches premium voice ON for everyone. Routes
// through /api/s/{slug}/voice/crowdfund-checkout (guest-friendly Stripe charge).
// =====================================================================
export default function FundVoiceModal({ open, slug, creatorName, onClose }) {
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [bounds, setBounds] = useState({ min: 500, max: 10000000 });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get(`/s/${slug}`);
        if (r.data?.tip_min_cents) setBounds({ min: r.data.tip_min_cents, max: r.data.tip_max_cents || 10000000 });
      } catch { /* defaults are fine */ }
    })();
  }, [open, slug]);

  if (!open) return null;

  const submit = async () => {
    const cents = custom ? Math.round(parseFloat(custom) * 100) : amount;
    if (!Number.isFinite(cents) || cents < bounds.min || cents > bounds.max) {
      return toast.error(`Amount must be between $${bounds.min / 100} and $${(bounds.max / 100).toLocaleString()}.`);
    }
    setBusy(true);
    try {
      const r = await api.post(`/s/${slug}/voice/crowdfund-checkout`, {
        amount_cents: cents, origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Could not start checkout.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[140] bg-black/50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} data-testid="fund-voice-modal"
      >
        <motion.div
          className="modal-safe bg-white rounded-xl border hairline shadow-2xl w-full max-w-md p-6"
          initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
              <SpeakerHigh size={22} weight="duotone" className="text-[#4A5D4E]" />
              Switch on premium voice
            </h3>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F0F1EE]" data-testid="fund-voice-close">
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-[#6B6A68] mb-4">
            {creatorName || "This creator"} hasn't funded premium AI voice yet. Your
            contribution tops up their voice credit — <strong>it's not a cash tip</strong> —
            and switches premium voice on for you and everyone after. 100% goes to the
            voice credit (no platform skim).
          </p>

          <div className="grid grid-cols-4 gap-2 mb-3" data-testid="fund-voice-presets">
            {PRESETS.map((c) => (
              <button key={c} onClick={() => { setAmount(c); setCustom(""); }}
                data-testid={`fund-voice-preset-${c}`}
                className={`py-2 rounded-md border text-sm font-medium ${(!custom && amount === c) ? "border-[#4A5D4E] bg-[#E8EBE5] text-[#4A5D4E]" : "hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E]"}`}
              >${c / 100}</button>
            ))}
          </div>

          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Custom amount</label>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[#6B6A68]">$</span>
            <input type="number" min={bounds.min / 100} max={bounds.max / 100} step="1"
              value={custom} onChange={(e) => setCustom(e.target.value)}
              placeholder="Enter amount" data-testid="fund-voice-custom"
              className="flex-1 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          </div>

          <button onClick={submit} disabled={busy} data-testid="fund-voice-submit"
            className="w-full text-sm py-3 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center justify-center gap-1.5">
            <CreditCard size={14} weight="duotone" />
            {busy ? "Loading Stripe…" : `Fund voice — $${(custom ? parseFloat(custom || "0") : amount / 100).toFixed(2)}`}
          </button>
          <p className="text-[10px] text-[#6B6A68] mt-2 text-center flex items-center justify-center gap-1">
            <ArrowSquareOut size={9} /> Secure checkout via Stripe
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
